import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { Anchor, Heading, Paragraph } from '@zillow/constellation';
import BlockedUserModal from './BlockedUserModal';
import pathsManifest from '../../../pathsManifest';
import { Auth } from '../../../AuthProvider';

function getSignInUrl(brand, currentPathname, queryString, isInWebView) {
  const returnPath = currentPathname + (queryString || '');
  if (isInWebView && brand === 'zillow') {
    return `/renter-hub/applications/nativeAppSignInPage?url=${encodeURIComponent(returnPath)}`;
  }
  return pathsManifest.signInPage[brand](returnPath);
}

BlockedUserContent.propTypes = {
  brand: PropTypes.oneOf(['zillow', 'hotpads', 'trulia']).isRequired,
};

function BlockedUserContent({ brand }) {
  const email = brand === 'zillow' ? 'account_verification@zillow.com' : 'account_verification@hotpads.com';
  const brandText = brand === 'zillow' ? 'Zillow' : brand === 'trulia' ? 'Trulia' : 'HotPads';

  return (
    <div>
      <Heading level="5" marginBottom="md">
        Your account is currently disabled
      </Heading>
      <Paragraph marginBottom="md">
        We are unable to access your properties at this time. Please contact{' '}
        <Anchor href={`mailto:${email}`}>{email}</Anchor> to resolve the issue.
      </Paragraph>
      <Paragraph>
        Return to <Anchor href={`https://www.${brand.toLowerCase()}.com`}>{brandText}</Anchor>
      </Paragraph>
    </div>
  );
}

const AuthCheckWrapper = ({ brand, children, isInWebView, router }) => {
  // Custom redirect logic for renter-hub-web
  const redirectHref = useCallback(
    (href, params) => {
      const queryString = params?.toString() ? `?${params.toString()}` : '';
      return getSignInUrl(brand, router.location.pathname, queryString || router.location.search, isInWebView);
    },
    [brand, router.location.pathname, router.location.search, isInWebView],
  );

  // Handle blocked users and SSR redirects for unauthorized users
  const onUnauthorized = useCallback(
    (user) => {
      // Handle blocked users (impersonators bypass this check)
      const isImpersonating = Boolean(user?.impersonatorUserToken && user?.impersonatorEmail);
      if (user?.isUserBlocked && !isImpersonating) {
        return <BlockedUserModal body={<BlockedUserContent brand={brand} />} />;
      }

      // Redirect non-logged-in users to sign-in
      const to = getSignInUrl(brand, router.location.pathname, router.location.search, isInWebView);
      return (
        <Route
          render={({ staticContext }) => {
            if (staticContext) staticContext.statusCode = 302;
            return <Redirect to={to} />;
          }}
        />
      );
    },
    [brand, isInWebView, router.location.pathname, router.location.search],
  );

  return (
    <Auth.Boundary redirectHref={redirectHref} onUnauthorized={onUnauthorized}>
      {children}
    </Auth.Boundary>
  );
};

AuthCheckWrapper.propTypes = {
  brand: PropTypes.oneOf(['zillow', 'hotpads', 'trulia']),
  children: PropTypes.node.isRequired,
  isInWebView: PropTypes.bool,
  router: PropTypes.shape({
    location: PropTypes.shape({
      hash: PropTypes.string,
      key: PropTypes.string,
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }),
};

const mapStateToProps = (state) => ({
  brand: state.app.brand,
  router: state.router,
  isInWebView: state.app.isInWebView,
});

export default connect(mapStateToProps)(AuthCheckWrapper);
